import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { animationFrameScheduler, auditTime, combineLatestWith, interval, Observable, pairwise, scan } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, tap } from 'rxjs/operators';
import { Actions, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Location } from '../../../models/locations-models/location';
import { Dictionary, EntityState } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import { selectAddressTranslation, selectFocusedRowId, selectHoveredRowId, selectSelectedRowsIds } from './state/location-list-selectors';
import { AddressTranslationActions, CCCompletionActions, EditGbpConnectionsActions, EditLocationActions, LocationsRefreshActions, RowActions } from './state/location-list-actions';
import { LSCField } from '../../../models/LSCFields';
import { DATA_STALE_AFTER_HR, MAX_JAVA_INT, SEARCH_DEFAULTS } from '../constants';
import { FilterModel } from '../../../models/locations-models/filter-map';
import { columnStateAdapter, getInitialColumnState } from './state/location-list-adapters';
import { ColumnState, IRowNode } from 'ag-grid-community';
import { getFromLocalStorage, sendToGoogleAnalytics, setToLocalStorage } from '../../../core/utilities';
import { LocalStorageKeys } from '../enums/local-storage-keys';
import { LocationListApiService } from './location-list-api.service';
import { addOrRemoveRowClass } from './location-list-utils';
import { isEqual } from 'lodash';
import { getFullNameForFieldID, hasBlacklistedFields, hasDifferences } from '../location-utils';
import { CCListRefreshActions, CCOperationActions } from '../cc-list/state/cc-list-actions';
import { Bubbles } from '../../../models/locations-models/bubbles';
import { StatusFilterEnum } from '../enums/status-filter-and-counters/status-filter-enum';
import { ManagementStatusListEnum } from '../enums/management-status-list-enum';
import { BusinessStatusListEnum } from '../enums/business-status-list-enum';
import { selectContinueAfterClaim, selectContinueAfterCreate } from '../cc-list/state/cc-list-selectors';
import { FeedbackService } from '../../../core/feedback.service';
import { CC_OPERATIONS } from '../../../models/locations-models/cc-row-setting';


export interface RowMenuPosition {
  left: number,
  top: number
}

export interface ContextMenuState {
  rowMenuPosition: RowMenuPosition;
  // since component store isn't connected to Redux DEV tools, storing functions should be OK,
  // this is basically just a behavior subject based service
  overlayRightClickListenerCB: Function;
}

export interface ColState extends EntityState<ColumnState>{}

export interface LocationListLocalState {
  contextMenuState: ContextMenuState,
  loadingValues: Dictionary<Dictionary<boolean>>, // {locationId: {LSCField: boolean}}
  loadingCcCompletion: Dictionary<boolean>,
  loadingTranslations: Dictionary<boolean>,
  showTranslations: Dictionary<boolean>,
  imagePreviewURL: string,

  total: number,
  pageIndex: number,
  pageSize: number,
  itemsPerPage: number[],

  filterModel: FilterModel,
  bubbles: Bubbles
  columnState: ColState,
  focussedCellValue: string,

  // Because we're rehydrating grids internal column state from browser cache in the onGridReady()
  // handler by calling applyColumnState(), this also causes other column-related events (sortChanged,
  // visibleChanged, etc.) to fire if there was a change VS default ColumnDefs, needlessly.
  // This flag can be used to filter out these events on component start
  gridReady: boolean,

  lastSuccessfulLocationsRefreshTimestamp: number,
}

@Injectable()
export class LocationListLocalStoreService extends ComponentStore<LocationListLocalState> {

  constructor(private globalActions$: Actions,
              private store: Store,
              private grid: LocationListApiService,
              private locationListApi: LocationListApiService,
              private feedbackService: FeedbackService) {
    super({
      contextMenuState: {
        rowMenuPosition: { left: 0, top: 0 },
        overlayRightClickListenerCB: null,
      },
      // determines which comment text fields will show a loading spinner
      loadingValues: {},
      loadingCcCompletion: {},
      loadingTranslations: {},
      showTranslations: {},
      imagePreviewURL: '',
      total: 0,
      pageIndex: 0,
      pageSize: SEARCH_DEFAULTS.PAGINATOR.PAGE_SIZE,
      itemsPerPage: SEARCH_DEFAULTS.PAGINATOR.ITEMS_PER_PAGE,
      filterModel: getFromLocalStorage(LocalStorageKeys.FILTER_STATE_KEY) ?
          getFromLocalStorage(LocalStorageKeys.FILTER_STATE_KEY) : {},
      bubbles: {
        [StatusFilterEnum.IS_DIFFERENT_IN_MY_BUSINESS]: 0,
        [StatusFilterEnum.IS_NOT_FOUND_IN_MY_BUSINESS]: 0,
        [StatusFilterEnum.IS_NOT_VERIFIED]: 0,
        [ManagementStatusListEnum.MANUALLY_MANAGED]: 0,
        [ManagementStatusListEnum.NOT_MANAGED]: 0,
        [StatusFilterEnum.HAS_IGNORED_DIFFERENCES]: 0,
        [StatusFilterEnum.HAS_COMPLETED_CC]: 0,
        [StatusFilterEnum.HAS_UPDATE_ERROR]: 0,
        [BusinessStatusListEnum.CLOSED_TEMPORARILY]: 0,
        [StatusFilterEnum.HAS_BLACKLISTED_FIELDS]: 0,
        [BusinessStatusListEnum.CLOSED_PERMANENTLY]: 0,
        [StatusFilterEnum.HAS_IMPORT_ERROR]: 0,
      },
      columnState: getInitialColumnState(),
      focussedCellValue: null,
      gridReady: false,
      lastSuccessfulLocationsRefreshTimestamp: null,
    });

    this.removeSpinnerAfterBLSUpdate$();

    this.removeCcCompletionSpinnerAfterUpdate$(globalActions$.pipe(
        ofType(CCCompletionActions.updateCcCompletionTimestampSuccess, CCCompletionActions.updateCcCompletionTimestampError),
        map((action) => action.location)));

    this.setLoadingTranslation$(this.globalActions$);
    this.revertToOriginalLanguageAndClearCache$(this.globalActions$);
    this.persistColumnStateChange$(this.columnStateArray$);
    this.persistFilterStateChange$(this.filterModel$);
    this.setRowSelectionOnRowFocusChange$(this.store.select(selectFocusedRowId));
    this.removeRowFocus$(this.store.select(selectSelectedRowsIds));
    this.updateRowStylesOnRowFocusChange$(this.store.select(selectFocusedRowId));
    this.updateRowStylesOnRowHoverChange$(this.store.select(selectHoveredRowId));
    this.filterSelectedRows$(this.filterModel$);
    this.updateLastSuccessfulLocationsRefreshTimestamp$();
    this.logFilterChangesInGA$(this.filterModel$);
    this.logColumnStateChangesInGA$(this.columnStateArray$);
    this.selectAdditionalRowsAfterCCUpdate$(this.globalActions$);
    this.continueAfterCreate(this.globalActions$);
    this.continueAfterClaim(this.globalActions$);
    this.clearCachedStatesOnRefresh$(this.globalActions$);
  }

  private readonly columnStateSelectors = columnStateAdapter.getSelectors();
  private readonly colState$ = this.select((state) => state.columnState);

  // to show image preview in bottom left corner
  readonly imagePreviewURL$: Observable<string> = this.select(state => state.imagePreviewURL);

  readonly rowMenuPosition$: Observable<RowMenuPosition> =
      this.select(state => state.contextMenuState.rowMenuPosition);

  readonly loadingValues$: Observable<Dictionary<Dictionary<boolean>>> =
      this.select(state => state.loadingValues);

  readonly loadingCcCompletion$: Observable<Dictionary<boolean>> =
      this.select(state => state.loadingCcCompletion);

  readonly loadingTranslations$: Observable<Dictionary<boolean>> =
      this.select(state => state.loadingTranslations);

  readonly showTranslations$: Observable<Dictionary<boolean>> =
      this.select(state => state.showTranslations);

  readonly total$: Observable<number> =
      this.select(state => state.total);

  readonly pageIndex$: Observable<number> =
      this.select(state => state.pageIndex);

  readonly pageSize$: Observable<number> =
      this.select(state => state.pageSize);

  readonly itemsPerPage$: Observable<number[]> =
      this.select(state => state.itemsPerPage);

  readonly filterModel$: Observable<FilterModel> =
      this.select(state => state.filterModel);

  readonly filtersCount$: Observable<number> =
      this.select(state => state.filterModel && Object.keys(state.filterModel).length);

  readonly columnStateArray$ = this.select(this.colState$, this.columnStateSelectors.selectAll);

  readonly columnStateDictionary$ = this.select(this.colState$, this.columnStateSelectors.selectEntities);

  readonly columnState$ = (field: string) => this.select(this.columnStateDictionary$,
      (columnStateDictionary) => columnStateDictionary[field]);

  readonly focussedCellValue$ = this.select(state =>
      state.focussedCellValue);

  readonly isSortingApplied$: Observable<boolean> = this.columnStateArray$.pipe(map(colState => {
    return !!colState.filter(col => col.sort).length;
  }));

  readonly displayedColumns$: Observable<string[]> =
      this.columnStateArray$.pipe(map((colState) => colState
          .filter(col => !col.hide)
          .map(col => col.colId)));

  readonly gridReady$: Observable<boolean> =
      this.select(state => state.gridReady);

  readonly potentiallyStaleList$: Observable<boolean> =
      this.select(state => state.lastSuccessfulLocationsRefreshTimestamp).pipe(
          combineLatestWith(interval(60000) // check every 60s or if timestamp is updated
              .pipe(startWith(-1))), // emit first value right away
          map(([timestamp, _]) => {
            if (!timestamp) return false;
            const currentTimeMs = new Date().getTime();
            const stalePeriodMs = DATA_STALE_AFTER_HR * 60 * 60 * 1000;
            const lastSuccessfulRefreshMs = new Date(timestamp)?.getTime();

            return (lastSuccessfulRefreshMs + stalePeriodMs) < currentTimeMs;
          }),
      )

  readonly bubbles$: Observable<Bubbles> =
      this.select(state => state.bubbles);


  // UPDATERS

  readonly updateMenuPosition = this.updater((
      state, rowMenuPosition: RowMenuPosition): LocationListLocalState =>
      ({
        ...state,
        contextMenuState: { ...state.contextMenuState, rowMenuPosition },
      }));

  readonly updateListenerCB = this.updater((
      state, overlayRightClickListenerCB: Function): LocationListLocalState =>
      ({
        ...state,
        contextMenuState: {
          ...state.contextMenuState,
          overlayRightClickListenerCB,
        },
      }));

  /**
   * add a comment loading spinner to a given location
   */
  readonly startLoadingValue = this.updater(
      (state, params: {locationId: number, field: LSCField}): LocationListLocalState => {
        const newValue = {[params.locationId]: {[params.field]: true}}
        return {
          ...state,
          loadingValues: { ...state.loadingValues, ...newValue },
        };
      });

  /**
   * Remove comment loading spinner for a given location
   */
  readonly stopLoadingValue = this.updater(
      (state, params: {locationId: number, field: LSCField}): LocationListLocalState => {
        const loadingValues = { ...state.loadingValues };
        const locationFields = { ...loadingValues[params.locationId] };
        
        delete locationFields[params.field];
        
        if (Object.keys(locationFields).length === 0) {
          delete loadingValues[params.locationId];
        } else {
          loadingValues[params.locationId] = locationFields;
        }
        
        return {
          ...state,
          loadingValues,
        };
      });

  /**
   * add a cc-completion loading spinner to a given location
   */
  readonly addCcCompletionSpinner = this.updater(
      (state, locationId: number): LocationListLocalState => {
        return {
          ...state,
          loadingCcCompletion: { ...state.loadingCcCompletion, [locationId]: true },
        };
      });

  /**
   * Remove cc-completion loading spinner for a given location
   */
  readonly removeCcCompletionSpinner = this.updater(
      (state, locationId: number): LocationListLocalState => {
        const loadingCcCompletion = { ...state.loadingCcCompletion };
        delete loadingCcCompletion[locationId];
        return {
          ...state,
          loadingCcCompletion,
        };
      });

  readonly setLoadingTranslation = this.updater(
      (state, config: { locationId: number, value: boolean }): LocationListLocalState => {
        const loadingTranslations = { ...state.loadingTranslations };
        if (config.value) {
          loadingTranslations[config.locationId] = true;
        } else {
          delete loadingTranslations[config.locationId];
        }
        return {
          ...state,
          loadingTranslations,
        };
      });

  readonly toggleShowTranslation = this.updater(
      (state, locationId: number): LocationListLocalState => {
        return {
          ...state,
          showTranslations: {
            ...state.showTranslations,
            [locationId]: !state.showTranslations[locationId],
          },
        };
      });

  readonly clearCachedStatesForIds = this.updater(
      (state, locationIds: (number | string)[]): LocationListLocalState => {
        const loadingTranslations = { ...state.loadingTranslations };
        const showTranslations = { ...state.showTranslations };
        const loadingValues = { ...state.loadingValues };
        const loadingCcCompletion = { ...state.loadingCcCompletion };

        for (const id of locationIds) {
          delete loadingTranslations[id];
          delete showTranslations[id];
          delete loadingValues[id];
          delete loadingCcCompletion[id];
        }

        return {
          ...state,
          loadingTranslations,
          showTranslations,
          loadingValues,
          loadingCcCompletion,
        };
      });

  readonly updateImagePreviewURL = this.updater(
      (state, imagePreviewURL: string): LocationListLocalState => {
        return {
          ...state,
          imagePreviewURL
        };
      });

  readonly updateTotal = this.updater(
      (state, total: number): LocationListLocalState => {
        return {
          ...state,
          total
        };
      });

  readonly updatePageIndex = this.updater(
      (state, pageIndex: number): LocationListLocalState => {
        return {
          ...state,
          pageIndex
        };
      });

  readonly updatePageSize = this.updater(
      (state, pageSize: number): LocationListLocalState => {
        return {
          ...state,
          pageSize
        };
      });

  readonly updateItemsPerPage = this.updater(
      (state, itemsPerPage: number[]): LocationListLocalState => {
        return {
          ...state,
          itemsPerPage
        };
      });

  readonly updateFilterModel = this.updater(
      (state, filterModel: FilterModel): LocationListLocalState => {
        return {
          ...state,
          filterModel
        };
      });

  readonly updateColumnState = this.updater(
      (state, columnState: ColumnState[]): LocationListLocalState => {
        return {
          ...state,
          columnState: columnStateAdapter.setAll(columnState, state.columnState)
        };
      });

  readonly updateFocussedCellValue = this.updater(
      (state, focussedCellValue: string): LocationListLocalState => {
        return {
          ...state,
          focussedCellValue
        };
      });

  readonly updateGridReady = this.updater(
      (state, gridReady: boolean): LocationListLocalState => {
        return {
          ...state,
          gridReady
        };
      });

  readonly updateLastSuccessfulLocationsRefreshTimestamp = this.updater(
      (state): LocationListLocalState => {
        return {
          ...state,
          lastSuccessfulLocationsRefreshTimestamp: new Date().getTime()
        };
      });


  readonly updateBubbles = this.updater(
      (state, bubbles: Bubbles): LocationListLocalState => {
        return {
          ...state,
          bubbles
        };
      });


  // EFFECTS


  /**
   * Remove listener upon closing the menu
   */
  readonly removeOverlayRightClickListener$ = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
        concatLatestFrom(() => this.state$),
        tap(([_, state]) => {
              if (state.contextMenuState.overlayRightClickListenerCB) {
                state.contextMenuState.overlayRightClickListenerCB();
                this.updateListenerCB(null);
              }
            },
        ));
  });

  /**
   * Remove loading spinner from editable text fields after a given location is successfully
   * updated/fails to update
   */
  readonly removeSpinnerAfterBLSUpdate$ = this.effect(() => {
    return this.globalActions$.pipe(
        ofType(EditLocationActions.editLocationDataSuccess, EditLocationActions.editLocationDataError),
        map((action) => {
          this.stopLoadingValue({locationId: action.location.id, field: action.singleField});
        })
    )
  });

  /**
   * Remove loading spinner from cc-completion button after a given location is successfully
   * updated/fails to update
   */
  readonly removeCcCompletionSpinnerAfterUpdate$ = this.effect((trigger$: Observable<Location>) => {
    return trigger$.pipe(tap({
          next: (location: Location) => {
            this.removeCcCompletionSpinner(location.id);
          },
        },
    ));
  });

  /**
   * Handle loading indication when translations are loading.
   */
  readonly setLoadingTranslation$ = this.effect((actions$: Actions) => {
    return actions$.pipe(
        ofType(AddressTranslationActions.getAddressTranslation,
            AddressTranslationActions.getAddressTranslationSuccess,
            AddressTranslationActions.getAddressTranslationFail),
        map((action) => {
          switch (action.type) {
            case AddressTranslationActions.getAddressTranslation.type:
              this.setLoadingTranslation({ locationId: action.id, value: true });
              break;
            case AddressTranslationActions.getAddressTranslationSuccess.type:
              this.setLoadingTranslation({
                locationId: action.addressTranslation.id,
                value: false,
              });
              break;
            case AddressTranslationActions.getAddressTranslationFail.type:
              this.setLoadingTranslation({ locationId: action.id, value: false });
              break;
          }
        }),
    );
  });

  /**
   * Toggle show translation. If new state is true, and we do not have a translation obj, fetch it.
   */
  readonly toggleShowTranslation$ = this.effect((locationId$: Observable<number>) => {
    return locationId$.pipe(
        concatLatestFrom((locationId) => this.store.select(selectAddressTranslation(locationId))),
        map(([locationId, addressTranslation]) => {
          this.toggleShowTranslation(locationId);
          if (!addressTranslation) {
            this.store.dispatch(AddressTranslationActions.getAddressTranslation({ id: locationId }));
          }
        }),
    );
  });

  /**
   *  If getting a translation obj failed, revert back to displaying the original strings
   *  and make sure we clear cache
   */
  readonly revertToOriginalLanguageAndClearCache$ = this.effect((actions$: Actions) => {
    return actions$.pipe(
        ofType(AddressTranslationActions.getAddressTranslationFail),
        concatLatestFrom(() => this.showTranslations$),
        map(([action, showTranslations]) => {
          if (showTranslations[action.id]) {
            this.toggleShowTranslation(action.id);
          }

          this.store.dispatch(AddressTranslationActions.removeAddressTranslation({ id: action.id }));
        }),
    );
  });

  /**
   *  Save column state to local storage
   */
  readonly persistColumnStateChange$ = this.effect((columnState: Observable<ColumnState[]>) => {
    return columnState.pipe(
        concatLatestFrom(() => this.gridReady$),
        // persist column state only after grid is completely initialized
        filter(([_, gridReady]) => gridReady),
        tap(([columnState, _]) => {
          setToLocalStorage(LocalStorageKeys.COLUMN_STATE_KEY, columnState);
        }));
  });

  /**
   *  Save filter state to local storage
   */
  readonly persistFilterStateChange$ = this.effect((filterState: Observable<FilterModel>) => {
    return filterState.pipe(
        concatLatestFrom(() => this.gridReady$),
        // persist column state only after grid is completely initialized
        filter(([_, gridReady]) => gridReady),
        tap(([filterModel, _]) => {
          setToLocalStorage(LocalStorageKeys.FILTER_STATE_KEY, filterModel);
        }));
  });

  /**
   * Makes sure only the focused row is also selected at the same time when focus changes
   */
  readonly setRowSelectionOnRowFocusChange$ = this.effect((focusedRowId: Observable<number>) => {
    return focusedRowId.pipe(
        filter(focusedRowId => !!focusedRowId),
        tap((focusedRowId: number) => {
          const selectedNodes: IRowNode<Location>[] = [];
          const deselectedNodes: IRowNode<Location>[] = [];

          this.grid.gridApi?.forEachNode(n => n.id === focusedRowId.toString(10) ?
              selectedNodes.push(n) : deselectedNodes.push(n));

          this.grid.gridApi?.setNodesSelected({ nodes: selectedNodes, newValue: true });
          this.grid.gridApi?.setNodesSelected({ nodes: deselectedNodes, newValue: false });
        }));
  });

  /**
   * Additionally select LSC locations after CC list update
   */
  readonly selectAdditionalRowsAfterCCUpdate$ = this.effect((actions$: Actions) => {
    return actions$.pipe(
        ofType(CCListRefreshActions.updateCCListSuccess),
        map((action) => {
          // select additional rows
          const selectedNodes: IRowNode<Location>[] = [];

          this.locationListApi.gridApi?.forEachNode(n => {
            if (action.lscLocationIdsToBeSelected?.includes(Number(n.id))) {
              selectedNodes.push(n);
            }
          });

          this.locationListApi.gridApi?.setNodesSelected({ nodes: selectedNodes, newValue: true });
        }));
  });

  /**
   * Edge-case: Makes sure to un-focus a focused row if it is deselected.
   */
  readonly removeRowFocus$ = this.effect((selectedRowsIds$: Observable<number[]>) => {
    return selectedRowsIds$.pipe(
        concatLatestFrom(() => this.store.select(selectFocusedRowId)),
        tap(([selectedRowsIds, focusedRowId]) => {
          if (focusedRowId && !selectedRowsIds.includes(focusedRowId)) {
            this.store.dispatch(RowActions.updateFocusedRow({id: null}));
          }
        }));
  });

  /**
   * Handle focused row styling
   */
  readonly updateRowStylesOnRowFocusChange$ = this.effect((focusedRowId$: Observable<number>) => {
    return focusedRowId$.pipe(
        pairwise(),
        map(([prevId, currId]) => {
          // 1. Remove class from all segments of the previous row
          if (prevId != null) {
            addOrRemoveRowClass(prevId, 'focusedRow', 'remove');
          }
          // 2. Add class to all segments of the new row
          if (currId != null) {
            addOrRemoveRowClass(currId, 'focusedRow', 'add');
          }
        })
    );
  });

  /**
   * Handle hovered row styling
   */
  readonly updateRowStylesOnRowHoverChange$ = this.effect((hoveredRowId$: Observable<number>) => {
    return hoveredRowId$.pipe(
        auditTime(0, animationFrameScheduler), // Batch emissions to the next paint cycle
        distinctUntilChanged(), // Only emit if the ID is different from the last emission
        pairwise(),
        map(([prevId, currId]) => {
          // 1. Remove class from all segments of the previous row
          if (prevId != null) {
            addOrRemoveRowClass(prevId, 'hoveredRow', 'remove');
          }
          // 2. Add class to all segments of the new row
          if (currId != null) {
            addOrRemoveRowClass(currId, 'hoveredRow', 'add');
          }
        })
    );
  });

  /**
   * Makes sure that the behavior set by 'headerCheckboxSelectionFilteredOnly: true' and
   * 'headerCheckboxSelectionCurrentPageOnly: true' is consistent when user changes page, page size
   * or filters/sorting
   */
  readonly filterSelectedRows$ = this.effect((filterModel$: Observable<FilterModel>) => {
    return filterModel$.pipe(
        combineLatestWith(this.pageSize$, this.pageIndex$, this.columnStateArray$),
        tap(([_, pageSize, pageIndex, colStateArray]) => {
          if (!this.locationListApi.gridApi) {
            return;
          }

          const paginationEnabled = pageSize !== MAX_JAVA_INT;
          const toBeSelectedNodes: IRowNode<Location>[] = [];
          const toBeDeselectedNodes: IRowNode<Location>[] = [];
          const displayedRowIndexes: number[] = [];

          if (paginationEnabled) {
            for (let i = pageIndex * pageSize; i < (pageIndex + 1) * pageSize; i++) {
              displayedRowIndexes.push(i);
            }
          }

          this.locationListApi.gridApi.forEachNode((rowNode) => {
            const isOnCurrentPage = paginationEnabled ?
                displayedRowIndexes.includes(rowNode.rowIndex) : true;

            if (rowNode.displayed && rowNode.isSelected() && isOnCurrentPage) {
              toBeSelectedNodes.push(rowNode);
            } else toBeDeselectedNodes.push(rowNode);
          });

          this.locationListApi.gridApi.setNodesSelected({nodes: toBeSelectedNodes, newValue: true});
          this.locationListApi.gridApi.setNodesSelected({nodes: toBeDeselectedNodes, newValue: false});
        }));
  });

  /**
   * Updates timestamp. LastSuccessfulLocationsRefreshTimestamp is used to determine if data is
   * potentially stale and should be refreshed by user.
   */
  readonly updateLastSuccessfulLocationsRefreshTimestamp$ = this.effect(() => {
    return this.globalActions$.pipe(
        ofType(LocationsRefreshActions.getEntireLocationListSuccess),
        tap(() => {
          this.updateLastSuccessfulLocationsRefreshTimestamp();
        })
    )
  });

  readonly logFilterChangesInGA$ = this.effect((filterModel$: Observable<FilterModel>) => {
    return filterModel$.pipe(
        scan((acc: {previous: FilterModel, current: FilterModel}, current: FilterModel) => {
          return {
            previous: acc?.current || {},
            current: current
          };
        }),
        tap((filterState: {previous: FilterModel, current: FilterModel}) => {
          const previousFilterState = filterState.previous ? filterState.previous : {};
          const currentFilterState = filterState.current ? filterState.current : {};

          // take ids from the larger object
          const fieldIds = Object.keys(previousFilterState).length > Object.keys(currentFilterState).length ?
              Object.keys(previousFilterState) : Object.keys(currentFilterState);
          // track filter change
          fieldIds.forEach((fieldId) => {
            if (currentFilterState[fieldId] && !isEqual(previousFilterState[fieldId], currentFilterState[fieldId])) {
              sendToGoogleAnalytics('Filter locations', 'filter', 'Filter for '
                  + getFullNameForFieldID(fieldId));
            }
          });
        })
    )
  });

  readonly logColumnStateChangesInGA$ = this.effect((columnState$: Observable<ColumnState[]>) => {
    return columnState$.pipe(
        scan((acc: {previous: ColumnState[], current: ColumnState[]}, current: ColumnState[]) => {
          return {
            previous: acc?.current || current,
            current
          };
        }),
        tap((columnStateArr: {previous: ColumnState[], current: ColumnState[]}) => {
          if (columnStateArr.previous?.length === columnStateArr.current?.length) {

            columnStateArr.previous?.forEach((previousColumnState, i) => {

              const currentColumnState = columnStateArr.current
                  .find(currentColumnState => currentColumnState.colId === previousColumnState.colId);

              // check if column order has changed (only check visible columns)
              if (!previousColumnState.hide && !columnStateArr.current[i].hide &&
                  previousColumnState.colId !== columnStateArr.current[i].colId) {
                sendToGoogleAnalytics(
                    'Adapt column display',
                    'Drag column',
                    `Dragged ${getFullNameForFieldID(columnStateArr.current[i].colId)} to a new position`);
              }
              // check pin status
              if (previousColumnState.pinned !== currentColumnState.pinned) {
                sendToGoogleAnalytics(
                    'Adapt column display',
                    currentColumnState.pinned ? 'Pin' : 'Unpin',
                    (currentColumnState.pinned ? 'Pin ' : 'Unpin ') +
                    getFullNameForFieldID(currentColumnState.colId));
              }
              // check visibility
              if (previousColumnState.hide !== currentColumnState.hide) {
                sendToGoogleAnalytics(
                    'Adapt column display',
                    currentColumnState.hide ? 'Hide column' : 'Show column',
                    (currentColumnState.hide ? 'Hide column: ' : 'Show column: ') +
                    getFullNameForFieldID(currentColumnState.colId),
                );
              }
              // check sort
              if (previousColumnState.sort !== currentColumnState.sort &&
                  currentColumnState.sortIndex === 0) {
                sendToGoogleAnalytics('Sort locations', 'sort', 'Sort for '
                    + getFullNameForFieldID(currentColumnState.colId));
              }
              // check multi-column sort
              if (previousColumnState.sort !== currentColumnState.sort &&
                  currentColumnState.sortIndex > 0) {
                sendToGoogleAnalytics('Sort locations', 'sort multiple columns', 'Sort for '
                    + getFullNameForFieldID(currentColumnState.colId));
              }
            });
          }
        }));
  });

  readonly computeBubbles = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
        tap(() => {
          const bubbles: Bubbles = {
            [StatusFilterEnum.IS_DIFFERENT_IN_MY_BUSINESS]: 0,
            [StatusFilterEnum.IS_NOT_FOUND_IN_MY_BUSINESS]: 0,
            [StatusFilterEnum.IS_NOT_VERIFIED]: 0,
            [ManagementStatusListEnum.MANUALLY_MANAGED]: 0,
            [ManagementStatusListEnum.NOT_MANAGED]: 0,
            [StatusFilterEnum.HAS_IGNORED_DIFFERENCES]: 0,
            [StatusFilterEnum.HAS_COMPLETED_CC]: 0,
            [StatusFilterEnum.HAS_UPDATE_ERROR]: 0,
            [BusinessStatusListEnum.CLOSED_TEMPORARILY]: 0,
            [StatusFilterEnum.HAS_BLACKLISTED_FIELDS]: 0,
            [BusinessStatusListEnum.CLOSED_PERMANENTLY]: 0,
            [StatusFilterEnum.HAS_IMPORT_ERROR]: 0,
          }

          this.locationListApi.getFilteredLocations().forEach((filteredLocation) => {
            if (hasDifferences(filteredLocation, 'open')) {
              bubbles[StatusFilterEnum.IS_DIFFERENT_IN_MY_BUSINESS] = bubbles[StatusFilterEnum.IS_DIFFERENT_IN_MY_BUSINESS] + 1;
            }

            if (!filteredLocation?.foundInMyBusiness) {
              bubbles[StatusFilterEnum.IS_NOT_FOUND_IN_MY_BUSINESS] += 1;
            }

            if (!filteredLocation?.verified) {
              bubbles[StatusFilterEnum.IS_NOT_VERIFIED] += 1;
            }

            if (filteredLocation?.managementStatus === ManagementStatusListEnum.MANUALLY_MANAGED) {
              bubbles[ManagementStatusListEnum.MANUALLY_MANAGED] += 1;
            }

            if (filteredLocation?.managementStatus === ManagementStatusListEnum.NOT_MANAGED) {
              bubbles[ManagementStatusListEnum.NOT_MANAGED] += 1;
            }

            if (hasDifferences(filteredLocation, 'ignored')) {
              bubbles[StatusFilterEnum.HAS_IGNORED_DIFFERENCES] += 1;
            }

            if (filteredLocation?.claimAndCleanCompletionTimestamp) {
              bubbles[StatusFilterEnum.HAS_COMPLETED_CC] += 1;
            }

            if (filteredLocation?.lastUpdateErrorMessage) {
              bubbles[StatusFilterEnum.HAS_UPDATE_ERROR] += 1;
            }

            if (filteredLocation?.businessStatus === BusinessStatusListEnum.CLOSED_TEMPORARILY) {
              bubbles[BusinessStatusListEnum.CLOSED_TEMPORARILY] += 1;
            }

            if (filteredLocation?.businessStatus === BusinessStatusListEnum.CLOSED_PERMANENTLY) {
              bubbles[BusinessStatusListEnum.CLOSED_PERMANENTLY] += 1;
            }

            if (hasBlacklistedFields(filteredLocation)) {
              bubbles[StatusFilterEnum.HAS_BLACKLISTED_FIELDS] += 1;
            }

            if (filteredLocation?.lastImportErrorMessage) {
              bubbles[StatusFilterEnum.HAS_IMPORT_ERROR] += 1;
            }
          });

          this.updateBubbles(bubbles);
        }));
  });

  // if ContinueAfterCreate selected, focus the next location and bring it into view
  readonly continueAfterCreate = this.effect((actions$: Actions) => {
      return actions$.pipe(
          ofType(EditGbpConnectionsActions.editGbpConnectionsSuccess),
          concatLatestFrom(() => [this.store.select(selectContinueAfterCreate),
            this.store.select(selectFocusedRowId),
            this.total$,
          ]),
          filter(([action,
                    continueAfterCreate,
                    focusedRowId,
                    total]) =>
              action.command === 'create' &&
              continueAfterCreate),
          map(([action,
                 continueAfterCreate,
                 focusedRowId,
                 total]) => {
            const rowNode = this.locationListApi.getRowById(String(focusedRowId));

            if (rowNode?.rowIndex + 1 === total) {
              this.feedbackService.showInfoSnackbar({data: {userFriendlyMessage: 'End of list reached'}})
              return;
            }

            const nextRow = this.locationListApi.getRowByIndex(rowNode.rowIndex + 1);
            this.store.dispatch(RowActions.updateFocusedRow({id: Number(nextRow.id)}));
          })
      );
  });

  // if ContinueAfterClaim selected, focus the next location and bring it into view
  readonly continueAfterClaim = this.effect((actions$: Actions) => {
    return actions$.pipe(
        ofType(CCOperationActions.ccOperationSuccess),
        concatLatestFrom(() => [this.store.select(selectContinueAfterClaim),
          this.store.select(selectFocusedRowId),
          this.total$,
        ]),
        filter(([action,
                  continueAfterClaim,
                  focusedRowId,
                  total]) =>
            action.ccOperation === CC_OPERATIONS.CLAIM &&
            continueAfterClaim),
        map(([action,
               continueAfterClaim,
               focusedRowId,
               total]) => {
          const rowNode = this.locationListApi.getRowById(String(focusedRowId));

          if (rowNode?.rowIndex + 1 === total) {
            this.feedbackService.showInfoSnackbar({data: {userFriendlyMessage: 'End of list reached'}})
            return;
          }

          const nextRow = this.locationListApi.getRowByIndex(rowNode.rowIndex + 1);
          this.store.dispatch(RowActions.updateFocusedRow({id: Number(nextRow.id)}));
        })
    );
  });

  readonly clearCachedStatesOnRefresh$ = this.effect((actions$: Actions) => {
    return actions$.pipe(
        ofType(LocationsRefreshActions.getEntireLocationListSuccess,
            LocationsRefreshActions.removedLocations
        ),
        map((action) => {
          let idsToClear: (number | string)[] = [];
          switch (action.type) {
            case LocationsRefreshActions.getEntireLocationListSuccess.type:
              if (action.locations) {
                idsToClear = action.locations.map(l => l.id).filter(id => id != null);
              }
              break;
            case LocationsRefreshActions.removedLocations.type:
              if (action.ids) {
                idsToClear = action.ids;
              }
              break;
          }

          if (idsToClear.length > 0) {
            this.clearCachedStatesForIds(idsToClear);
          }
        })
    );
  });

}

